/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import {
  Row,
  Column,
  Text,
  Divider,
  Selector,
  Spacing,
  Button,
  withTheme,
  useDevice,
  Modal,
  Checkbox,
  StatusAlert,
  BulletList
} from '@dls/web';
import { Completed, Alert } from '@dls/assets/dist/icons/web';
import { useSelector, useDispatch } from 'react-redux';
import { checkoutActions, userActions } from '@detox/actions';
import useTranslate from '../../hooks/useTranslation';
import { FieldObject } from '../../types/registrationCheckout';
import Disclaimer from './Disclaimer';
import {
  CHOOSE_SIM_TYPE,
  ChooseSimType,
  SimTransactionType,
  SimTypeId,
  eSIMCompatibleTags
} from '../../constants/choose-sim-type';
import styled, { css } from 'styled-components';
import { scrollToSection } from '@common-modules/product-catalogue';
import { phoneTagsConfig } from '../../config/phone-config';
import { AEM_CONFIG_KEYS } from '../../config/common';
import {
  isNullOrUndefinedOrEmptyCheck,
  isTagged,
  getAemConfig
} from '../../helpers/common';
import CustomerPersonalDetails from './CustomerPersonalDetails';
import {
  KeyValue,
  APP_TYPE_ANY,
  AemVariableConfig
} from '../../types/common.types';
import { formattedPhoneNumber } from '../OrderSummary/ChangeContactDetails/helper';
import {
  FEATURE_RECON_PD_UPDATE,
  FEATURE_FLAG_SPECIAL_PROMO
} from '../../types/featureFlag.types';
import isFeatureFlagEnabled from '../../helpers/feature-flags';
import CONSTANTS from '../../constants/common';
import { getIfSimOnly } from '../../selectors';
import { getSimDetails } from '../../helpers/sim-card-option-helper';
import { ModalContent, PortInEsimTnc } from './PortInEsimTnc';

interface SimTypeDesignConfig {
  dividerNeeded: boolean;
  titlesNeeded: boolean;
  ctaNeeded: boolean;
  typeHeight: string;
  simTypeColumnProps: FieldObject;
  forceCostFree: boolean;
}

interface TProps {
  data: ChooseSimType[];
  showSubtitle?: boolean;
  updateSimTypeOnSelection?: boolean;
  preSelectedSimType?: SimTypeId;
  selectedProduct?: FieldObject;
  columnProps?: FieldObject;
  transactionTypeValue: SimTransactionType;
  simTypeDesignConfig?: Partial<SimTypeDesignConfig>;
  scrollToSimType?: boolean;
  onSimTypeOnSelection?: (selectedSimType: SimTypeId) => void;
  enablePDForm?: boolean;
  aemVariableConfigs?: AemVariableConfig[];
  btnText?: string;
  isPortIn?: boolean;
}

const StyledRow = styled(Row)`
  margin: 0 -8px;
`;

const StyledEsimCompatibilityRow = styled(Row)`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
`;

const StyledSpacing = withTheme(styled(Spacing)`
  ${({ coreTheme }) => css`
    height: ${({ typeHeight }) => `${typeHeight || 'auto'};`};
    @media (max-width: ${coreTheme.brk_sm}) {
      height: auto;
    }
  `}
`);

interface State {
  termsModalOpen: boolean;
  tncSelected: boolean;
  tncClicked: boolean;
  tncMessage: string;
  portInTransferSelected: boolean;
  portInTransferMessage: string;
}

const PortInPreCheck = ({
  portInTransferMessage,
  handleChange,
  t,
  selectedProduct
}) => {
  return (
    <>
      <Spacing topBottom={1}>
        {selectedProduct && (
          <Checkbox.InputGroup error={portInTransferMessage}>
            <Checkbox.Input
              error={!!portInTransferMessage}
              onChange={(e: any, isSelected: boolean) => {
                handleChange(isSelected);
              }}
              label={
                <>
                  <Text tag={'span'}>{t('PORT_IN_CHECKBOX_TEXT')}</Text>
                  <Text tag={'span'} type={'boldBody'}>
                    {t('PORT_IN_CHECKBOX_TEXT_BOLD')}
                  </Text>
                </>
              }
            />
          </Checkbox.InputGroup>
        )}

        <Spacing top={1}>
          <StatusAlert
            title={t('BEFORE_PORTIN_TITLE')}
            type="info"
            bodyContent={
              <>
                <BulletList noSpace>
                  <Text type="smallBody">
                    {t('PORT_NUMBER_MUST_BE_REGISTERED')}
                  </Text>
                </BulletList>
                <BulletList noSpace>
                  <Text type="smallBody">{t('PORT_IN_CONFIRM_CONTRACT')}</Text>
                </BulletList>
              </>
            }
          />
        </Spacing>
      </Spacing>
    </>
  );
};

export const ChooseSIMType = (props: TProps): ReactElement => {
  const dispatch = useDispatch();
  const { getUserInformation } = userActions;
  const formikRef = useRef<KeyValue>();
  const { userInformation, specialPromo, isSimOnly } = useSelector(
    (state: KeyValue) => ({
      userInformation: state?.user?.information,
      specialPromo: state?.promotions?.specialPromo,
      isSimOnly: getIfSimOnly(state)
    })
  );
  const { t } = useTranslate();
  const { isMobile } = useDevice();
  const {
    data,
    onSimTypeOnSelection,
    selectedProduct,
    columnProps,
    showSubtitle = false,
    transactionTypeValue = 'newSignup',
    simTypeDesignConfig,
    updateSimTypeOnSelection = false,
    preSelectedSimType = null,
    scrollToSimType = false,
    enablePDForm = false,
    aemVariableConfigs,
    btnText
  } = props;
  const imdaFeatureEnabled = isFeatureFlagEnabled(FEATURE_RECON_PD_UPDATE);
  const isSpecialPromoEnabled = isFeatureFlagEnabled(
    FEATURE_FLAG_SPECIAL_PROMO
  );
  const isRecontract =
    transactionTypeValue === CONSTANTS.TRANSACTION_TYPE_VALUE.RECONTRACT;
  const hasSpecialPromo = isSpecialPromoEnabled && specialPromo;
  const showCustomerPersonalDetails =
    imdaFeatureEnabled &&
    enablePDForm &&
    isRecontract &&
    userInformation?.userDetails?.loginId;
  const disableSOPEsim = aemVariableConfigs
    ? getAemConfig(
        aemVariableConfigs,
        AEM_CONFIG_KEYS.DETOX_DISABLE_ESIM_SOP_RECON
      ) === 'true'
    : false;
  const disableEsim = disableSOPEsim && isRecontract && isSimOnly;
  const {
    titlesNeeded = true,
    ctaNeeded = true,
    dividerNeeded = true,
    simTypeColumnProps = { xs: 12 },
    typeHeight,
    forceCostFree = false
  } = simTypeDesignConfig || {};
  const [selectedSimType, setSelectedSimType] = useState<SimTypeId>();

  const columnConfig = columnProps || { sm: 12, md: 8 };
  const isLifeStyleProduct = isTagged(
    selectedProduct?.tags || [],
    phoneTagsConfig['home-lifestyle'].tagName
  );
  const hasNoPhysicalSim = isTagged(
    selectedProduct?.tags || [],
    phoneTagsConfig['no-physical-sim'].tagName
  );

  const [state, _setState] = useState<State>({
    tncSelected: false,
    termsModalOpen: false,
    tncClicked: false,
    tncMessage: '',
    portInTransferSelected: false,
    portInTransferMessage: ''
  });

  const setState = (stateChange: Partial<State>) => {
    _setState({ ...state, ...stateChange });
  };

  let eSimData;

  const availableTypes = (data || [])
    .filter(availableType => {
      const simDetails = getSimDetails(availableType, transactionTypeValue);
      const transactionType = simDetails?.transactionType;
      const { simTypeId } = availableType;

      if (simTypeId === CHOOSE_SIM_TYPE.SIM_TYPE_ESIM && disableEsim) {
        return false;
      }

      if (
        simTypeId === CHOOSE_SIM_TYPE.SIM_TYPE_PhysicalSIM &&
        (hasNoPhysicalSim || (hasSpecialPromo && !specialPromo.showPhysicalSim))
      ) {
        return false;
      }

      if (simTypeId === selectedSimType) {
        eSimData = simDetails;
      }
      return transactionType === transactionTypeValue;
    })
    .sort((a, b) =>
      a.notAvailable === b.notAvailable ? 0 : a.notAvailable ? 1 : -1
    );

  const isESIMCompatible = (simSlotsperSIMCardType: string): boolean => {
    if (!simSlotsperSIMCardType) {
      return false;
    }

    return (
      Boolean(
        eSIMCompatibleTags.find(
          eSIMCompatibleTag => eSIMCompatibleTag === simSlotsperSIMCardType
        )
      ) || simSlotsperSIMCardType.includes('eSIM')
    );
  };

  const { simSlotsperSIMCardType, title: productTitle } = selectedProduct || {};
  const iseSIMCompatible = isESIMCompatible(simSlotsperSIMCardType);
  const shouldShowCompatibility =
    selectedProduct &&
    !isLifeStyleProduct &&
    isNullOrUndefinedOrEmptyCheck(simSlotsperSIMCardType, true) &&
    simSlotsperSIMCardType !== 'NA';

  useEffect(() => {
    // set selected sim type
    if (preSelectedSimType) {
      setSelectedSimType(preSelectedSimType);
    } else if (!availableTypes[0]?.notAvailable) {
      setSelectedSimType(availableTypes[0]?.simTypeId);
      !ctaNeeded && onSimTypeOnSelection(availableTypes[0]?.simTypeId);
    }

    scrollToSimType && scrollToSection('choose-sim-type');
  }, []);

  if (availableTypes.length === 0) {
    return <></>;
  }

  const TermsAndConditionsModal = () => (
    <Modal
      visible={state.termsModalOpen}
      title="Port in Details"
      onClose={() => setState({ termsModalOpen: false })}
    >
      <ModalContent />
    </Modal>
  );

  const isEsimSelected = selectedSimType === CHOOSE_SIM_TYPE.SIM_TYPE_ESIM;

  const handleSimTypeSelection = async () => {
    if (transactionTypeValue === 'portIn') {
      switch (true) {
        case !state.tncSelected &&
          !state.portInTransferSelected &&
          !!selectedProduct: {
          setState({
            tncMessage: t('PORT_IN_TRANSFER_CHECKBOX_ERROR'),
            portInTransferMessage: t('PORT_IN_TRANSFER_CHECKBOX_ERROR')
          });
          return;
        }
        case !state.portInTransferSelected && !!selectedProduct: {
          setState({
            portInTransferMessage: t('PORT_IN_TRANSFER_CHECKBOX_ERROR')
          });
          return;
        }
        case !state.tncSelected: {
          setState({
            tncMessage: t('PORT_IN_TRANSFER_CHECKBOX_ERROR')
          });
          return;
        }
      }
    }
    if (showCustomerPersonalDetails) {
      try {
        await formikRef.current?.submitForm();
      } catch (e) {
        console.error('Error submitting form:', e);
      }

      const isFormValid = formikRef?.current?.isValid;
      const { email, contactId, phone } =
        userInformation.clientContext.contact || {};
      const formValues = {
        ...formikRef.current.values,
        number: formattedPhoneNumber(formikRef.current.values.number),
        email: formikRef.current.values?.email
          ? formikRef.current.values.email.toLowerCase()
          : ''
      };
      if (
        isFormValid &&
        (email.toLowerCase() !== formValues.email ||
          phone !== formValues.number)
      ) {
        const response: KeyValue = await (dispatch(
          checkoutActions.updateCustomerContact({
            phone: formValues.number,
            emailId: formValues.email,
            contactId
          })
        ) as APP_TYPE_ANY);
        if (response?.implContactPerson) {
          await (dispatch(getUserInformation()) as APP_TYPE_ANY);
        } else {
          return;
        }
      } else if (!isFormValid) {
        return;
      }
    }
    onSimTypeOnSelection(selectedSimType);
  };

  const renderDisclaimer = () => {
    if (
      (selectedProduct && eSimData?.footerWithDevice) ||
      (!selectedProduct && eSimData?.footerWithoutDevice)
    ) {
      return (
        <Spacing top={1} bottom={isMobile ? 1 : 0}>
          <Disclaimer data={eSimData} isWithDevice={!!selectedProduct} />
        </Spacing>
      );
    }
    return null;
  };

  return (
    <Row id="choose-sim-type">
      <Column noGutter {...columnConfig}>
        {props.isPortIn && (
          <PortInPreCheck
            handleChange={(isSelected: boolean) => {
              setState({
                portInTransferSelected: isSelected,
                portInTransferMessage: ''
              });
            }}
            portInTransferMessage={state.portInTransferMessage}
            t={t}
            selectedProduct={selectedProduct}
          />
        )}
        {dividerNeeded && (
          <Spacing topBottom={3}>
            <Divider />
          </Spacing>
        )}
        {titlesNeeded && (
          <Spacing bottom={1}>
            <Text type="header">{t('CHOOSE_SIM_TYPE')}</Text>
            {showSubtitle && (
              <Text type="body">{t('SIM_TYPE_COMPATIBLE')}</Text>
            )}
          </Spacing>
        )}

        <StyledRow>
          {availableTypes.map(simType => {
            const simDetails = getSimDetails(simType, transactionTypeValue);
            const simTypePrice = simDetails.simPrice;
            const simTypePriceAvailable =
              simTypePrice !== null &&
              simTypePrice !== undefined &&
              simTypePrice !== '';
            const simPriceValue = simTypePrice ?? null;

            return (
              <Column {...simTypeColumnProps} key={simType.simTypeId}>
                <StyledSpacing typeHeight={typeHeight}>
                  <Selector
                    value={simType.simTypeId}
                    selected={selectedSimType === simType.simTypeId}
                    onClick={v => {
                      setSelectedSimType(v);
                      if (updateSimTypeOnSelection) {
                        onSimTypeOnSelection(v);
                      }
                      showCustomerPersonalDetails &&
                        scrollToSection('pd-phone');
                    }}
                    disabled={simType.notAvailable}
                  >
                    <Selector.Body
                      title={simType.simTitle}
                      subtitle={
                        selectedProduct
                          ? simDetails.descWithDevice
                          : simDetails.descWithoutDevice
                      }
                      align={'start'}
                    />

                    {simTypePriceAvailable && !forceCostFree && (
                      <Text>
                        {simPriceValue === 0
                          ? t('PRICE_FREE')
                          : `$${simPriceValue}`}
                      </Text>
                    )}
                  </Selector>
                  {simType.notAvailable && simType.footerNotAvailable && (
                    <Text type="body">
                      <span
                        dangerouslySetInnerHTML={{
                          __html: simType.footerNotAvailable
                        }}
                      />
                    </Text>
                  )}
                </StyledSpacing>
                {simType.simTypeId === CHOOSE_SIM_TYPE.SIM_TYPE_ESIM &&
                  shouldShowCompatibility &&
                  !simType.notAvailable && (
                    <Spacing top={isMobile ? 2 : 1}>
                      <StyledEsimCompatibilityRow>
                        {iseSIMCompatible ? <Completed size={24} /> : <Alert />}
                        <Spacing left={1} responsive={false}>
                          {iseSIMCompatible ? (
                            <Text>Your {productTitle} is eSIM-compatible</Text>
                          ) : (
                            <Text>
                              Your {productTitle} is not eSIM-compatible
                            </Text>
                          )}
                        </Spacing>
                      </StyledEsimCompatibilityRow>
                    </Spacing>
                  )}
              </Column>
            );
          })}
        </StyledRow>
        {renderDisclaimer()}
        {showCustomerPersonalDetails && (
          <CustomerPersonalDetails
            formikRef={formikRef}
            userInformation={{ ...userInformation.clientContext.contact }}
          />
        )}

        {props?.isPortIn && (
          <Spacing topBottom={3}>
            <Divider />
          </Spacing>
        )}

        <PortInEsimTnc
          transactionTypeValue={transactionTypeValue}
          selectedProduct={selectedProduct}
          tncMessage={state.tncMessage}
          onChange={isSelected => {
            setState({
              tncSelected: isSelected,
              tncMessage: ''
            });
          }}
          onTncClicked={() => setState({ termsModalOpen: true })}
        />

        {ctaNeeded && (
          <Spacing top={2} bottom={4}>
            <Button
              fullWidth={isMobile}
              primary
              onClick={() => handleSimTypeSelection()}
              data-testid="btn-choose-addons"
            >
              <Spacing leftRight={4}>
                {btnText ? btnText : t('CHOOSE_ADD_ONS')}
              </Spacing>
            </Button>
          </Spacing>
        )}
        {state.termsModalOpen && TermsAndConditionsModal()}
      </Column>
    </Row>
  );
};

export default ChooseSIMType;
